/**
 * PATH: packages/ui/src/kanban/components/DateField.tsx
 * FILE_ID: UI-KANBAN-DATE-FIELD-001
 * PURPOSE: A card date control you can TYPE into ("tomorrow", "next fri",
 *          "25/12") that also drops down a calendar to click a day.
 * WHY:
 *   Operator ask (FR card_composer_date_autofill, 2026-06-15):
 *   "When I am writing a date here on the card I should have a drop down
 *   selection with calendar autofill." A native <input type="date"> only
 *   offers the browser's segmented widget; you cannot type natural dates
 *   and there is no quick-preset dropdown. This replaces the native start
 *   and due inputs on the card with a single typeable + calendar field.
 *
 * FLOW:
 *   type text -> parseDateInput -> live suggestion + presets + month grid
 *             -> onChange(iso | null)
 *
 * DEPENDENCIES:
 *   - dateParse: pure typed-text -> ISO + calendar grid math (tested apart)
 *   - useViewportClampedMenuPosition: shared popover clamp (no off-screen)
 *   - createPortal: render the dropdown on document.body so it escapes the
 *     card drawer's overflow + transform context (same as the lane/board
 *     popovers).
 * RELATED:
 *   - CardDetailDrawer (start/due rows) consumes this.
 *   - ARC-6 (U05): exported as `SmartDateInput` — THE one shared
 *     natural-language date input for EVERY kanban date field (drawer rows,
 *     quick-action due picker, right-click date submenus, checklist item
 *     dates). Renders committed values day-first DD/MM/YYYY (en-AU) and
 *     parses "12/8", "12 Aug", "tomorrow", "next tue", "week 6", "in 3
 *     weeks" via dateParse (the single parser of record — never fork it).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useViewportClampedMenuPosition } from '../hooks/useViewportClampedMenuPosition'
import {
  parseDateInput,
  formatHumanDate,
  relativeDateHint,
  isoToDate,
  toISODate,
  startOfDay,
  addDays,
  addMonths,
  monthGrid,
  monthTitle,
  WEEKDAY_HEADERS,
} from '../dateParse'
import { formatDmy } from '../utils/localDate'

export interface DateFieldProps {
  /** Current value as an ISO calendar date (YYYY-MM-DD) or null. */
  value: string | null
  /** Called with the new ISO date, or null when cleared. */
  onChange: (iso: string | null) => void
  ariaLabel: string
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Injectable "now" for deterministic tests. */
  today?: Date
  /** FEAT-2026-07-07-KANBAN-CARD-ACTION-LINE — focus the field on mount
   *  (which also opens the picker via onFocus). For click-to-edit swap
   *  hosts (card-face date chips) where the field appears in place of a
   *  chip the operator just clicked. */
  autoFocus?: boolean
  /** Called when the operator leaves WITHOUT committing (Escape, outside
   *  click, or blur with no change) so swap hosts can restore the chip.
   *  A commit fires onChange only — hosts exit edit mode there. */
  onDismiss?: () => void
}

interface PresetDef {
  label: string
  iso: string
}

export function DateField({
  value,
  onChange,
  ariaLabel,
  placeholder = 'Type or pick a date',
  disabled = false,
  className = '',
  today,
  autoFocus = false,
  onDismiss,
}: DateFieldProps) {
  const now = useMemo(() => startOfDay(today ?? new Date()), [today])
  const inputRef = useRef<HTMLInputElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  // ARC-6 (U05): committed values render Australian day-first DD/MM/YYYY
  // (operator: "Dates should be in Australian format") via the ONE shared
  // formatter every kanban surface uses (utils/localDate.formatDmy). The
  // popover's live suggestion keeps the spelled-out weekday form as an
  // unambiguous confirmation of what was parsed.
  const [text, setText] = useState<string>(() => formatDmy(value))
  const [anchor, setAnchor] = useState({ x: 0, y: 0 })
  const [monthAnchor, setMonthAnchor] = useState<Date>(
    () => isoToDate(value) ?? now,
  )

  // Keep the visible text in sync when the value changes from outside
  // (another edit, a reset) while the field is not being typed in.
  useEffect(() => {
    if (!open) setText(formatDmy(value))
  }, [value, open])

  const pos = useViewportClampedMenuPosition({ anchor, menuRef: popRef })

  // autoFocus (click-to-edit swap hosts): focus once on mount — onFocus then
  // opens the picker exactly as a manual focus would.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design
  }, [])

  // Live interpretation of whatever is currently typed.
  const typedIso = useMemo(() => parseDateInput(text, now), [text, now])

  const presets: PresetDef[] = useMemo(
    () => [
      { label: 'Today', iso: toISODate(now) },
      { label: 'Tomorrow', iso: toISODate(addDays(now, 1)) },
      { label: 'In 1 week', iso: toISODate(addDays(now, 7)) },
    ],
    [now],
  )

  const grid = useMemo(() => monthGrid(monthAnchor, now), [monthAnchor, now])

  function openPopover() {
    if (disabled) return
    const rect = inputRef.current?.getBoundingClientRect()
    if (rect) setAnchor({ x: rect.left, y: rect.bottom + 4 })
    setMonthAnchor(isoToDate(value) ?? now)
    setOpen(true)
  }

  function commit(iso: string | null) {
    onChange(iso)
    setText(formatDmy(iso))
    setOpen(false)
    // Return focus to the field so keyboard users keep their place.
    inputRef.current?.blur()
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (typedIso) commit(typedIso)
    } else if (e.key === 'Escape') {
      setText(formatDmy(value))
      setOpen(false)
      onDismiss?.()
    } else if (e.key === 'ArrowDown' && !open) {
      openPopover()
    }
  }

  function handleInputBlur() {
    // Defer so a click inside the portaled popover registers first.
    window.setTimeout(() => {
      if (popRef.current?.contains(document.activeElement)) return
      if (text.trim() === '') {
        // Explicit emptying clears the date.
        if (value !== null) onChange(null)
      } else if (typedIso && typedIso !== value) {
        onChange(typedIso)
        setText(formatDmy(typedIso))
      } else {
        setText(formatDmy(value))
        onDismiss?.()
      }
    }, 120)
  }

  // Outside-click / Escape dismissal for the portaled popover.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      const t = e.target as Node
      if (popRef.current?.contains(t)) return
      if (inputRef.current?.contains(t)) return
      setOpen(false)
      setText(formatDmy(value))
      onDismiss?.()
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        setText(formatDmy(value))
        onDismiss?.()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open, value, onDismiss])

  const hint = relativeDateHint(value, now)

  return (
    // `min-w-0` is load-bearing, not tidying. This span is a flex ITEM of its
    // consumer's row, and a flex item defaults to `min-width:auto` — it refuses
    // to shrink below its content. The <input> inside carries `min-w-0` and the
    // row outside carries `min-w-0`, so the shrink chain was broken at exactly
    // this one link: the field stayed pinned to the input's ~150px intrinsic
    // width and punched out of the 320px card-detail rail on tablet, giving the
    // rail a horizontal scrollbar nested inside its vertical one. Only bites
    // when the row is too narrow to begin with, so wide desktop rows are
    // unaffected.
    <span className="relative inline-flex min-w-0 items-center">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          if (!open) openPopover()
        }}
        onFocus={openPopover}
        onClick={openPopover}
        onKeyDown={handleInputKeyDown}
        onBlur={handleInputBlur}
        className={className}
        data-testid="kanban-date-field-input"
      />
      <CalendarIcon
        className="pointer-events-none -ml-5 h-3.5 w-3.5 shrink-0 text-neutral-400"
        aria-hidden
      />

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label={`${ariaLabel} picker`}
            className="fixed z-[120] w-[260px] rounded-lg border border-neutral-200 bg-white p-2 shadow-2xl ring-1 ring-black/5 dark:border-zinc-800 dark:bg-zinc-950 dark:ring-white/10"
            style={{ left: pos.left, top: pos.top }}
            data-testid="kanban-date-field-popover"
            // ARC-6: this popover portals to <body>, so when the field is
            // embedded inside a capture-dismissed menu (quick-action picker,
            // right-click submenu) a click in here reads as "outside" to the
            // host's useDismissOnOutsideInteraction and would unmount the
            // field mid-interaction. shouldDismissContextMenu treats anything
            // inside [data-kanban-nested-popover] as inside the host menu.
            data-kanban-nested-popover=""
          >
            {/* Live interpretation of the typed text. */}
            {text.trim() !== '' && (
              <button
                type="button"
                disabled={!typedIso}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => typedIso && commit(typedIso)}
                className={[
                  'mb-1 flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-[12px]',
                  typedIso
                    ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300'
                    : 'cursor-default text-neutral-400',
                ].join(' ')}
                data-testid="kanban-date-field-parsed"
              >
                {typedIso ? (
                  <>
                    <span className="truncate">{formatHumanDate(typedIso)}</span>
                    <span className="ml-2 shrink-0 text-[10px] uppercase tracking-wide opacity-70">
                      Enter
                    </span>
                  </>
                ) : (
                  <span>Not a date - try "tomorrow", "week 6" or 25/12</span>
                )}
              </button>
            )}

            {/* Quick presets. */}
            <div className="mb-2 flex flex-wrap gap-1">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(p.iso)}
                  className="rounded-full border border-neutral-200 px-2 py-0.5 text-[11px] text-neutral-600 hover:bg-neutral-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-900"
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Month header + nav. */}
            <div className="mb-1 flex items-center justify-between px-1">
              <button
                type="button"
                aria-label="Previous month"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setMonthAnchor((m) => addMonths(m, -1))}
                className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-zinc-900"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-[12px] font-semibold text-neutral-700 dark:text-zinc-200">
                {monthTitle(monthAnchor)}
              </span>
              <button
                type="button"
                aria-label="Next month"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setMonthAnchor((m) => addMonths(m, 1))}
                className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-zinc-900"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Day-of-week headers. */}
            <div className="grid grid-cols-7 gap-0.5 px-1 pb-1">
              {WEEKDAY_HEADERS.map((h) => (
                <span
                  key={h}
                  className="text-center text-[10px] font-medium text-neutral-400"
                >
                  {h}
                </span>
              ))}
            </div>

            {/* Calendar grid. */}
            <div className="grid grid-cols-7 gap-0.5 px-1">
              {grid.flat().map((cell) => {
                const selected = value != null && cell.iso === value
                return (
                  <button
                    key={cell.iso}
                    type="button"
                    aria-label={formatHumanDate(cell.iso)}
                    aria-selected={selected}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => commit(cell.iso)}
                    className={[
                      'flex h-7 items-center justify-center rounded text-[12px] transition-colors',
                      selected
                        ? 'bg-blue-600 font-semibold text-white hover:bg-blue-600'
                        : cell.inMonth
                          ? 'text-neutral-700 hover:bg-neutral-100 dark:text-zinc-200 dark:hover:bg-zinc-900'
                          : 'text-neutral-300 hover:bg-neutral-100 dark:text-zinc-600 dark:hover:bg-zinc-900',
                      cell.isToday && !selected
                        ? 'ring-1 ring-inset ring-blue-400'
                        : '',
                    ].join(' ')}
                  >
                    {cell.day}
                  </button>
                )
              })}
            </div>

            {/* Footer: clear (only when a value is set). */}
            {value != null && (
              <div className="mt-2 flex items-center justify-between border-t border-neutral-100 px-1 pt-1.5 dark:border-zinc-800">
                <span className="text-[10px] text-neutral-400">{hint}</span>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(null)}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                  data-testid="kanban-date-field-clear"
                >
                  <X className="h-3 w-3" /> Clear
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </span>
  )
}

// ARC-6 (U05): canonical name for the one shared natural-language kanban date
// input. Alias — NOT a second implementation — so new call sites read as the
// arc's contract ("SmartDateInput everywhere") while existing DateField
// consumers keep working untouched (expand → migrate → contract).
export const SmartDateInput = DateField
export type SmartDateInputProps = DateFieldProps
